import axiosInstance from 'api/axiosConfig'
import { ENDPOINTS } from 'api/endpoints'
import { IdentityVerificationStatus, ListingManagerStatus } from 'types/workflowStatus'

export type VerificationRecord = {
  id: number
  user_fk: string
  verification_type: 'identity'
  status: IdentityVerificationStatus
  approved_by_user_fk?: string | null
  reviewed_by_user_fk?: string | null
  rejection_reason?: string | null
  internal_notes?: string | null
  visible_message?: string | null
  started_at?: string | null
  submitted_at?: string | null
  reviewed_at?: string | null
  approved_at?: string | null
  rejected_at?: string | null
  correction_requested_at?: string | null
  created_at?: string
  updated_at?: string
}

export type IdentityDocumentRecord = {
  id: number
  user_fk: string
  verification_id?: number | null
  document_type: string
  file_url?: string | null
  file_name?: string | null
  file_mime?: string | null
  file_size?: number | null
  status: IdentityVerificationStatus
  version: number
  correction_reason?: string | null
  reviewed_by_user_fk?: string | null
  reviewed_at?: string | null
  created_at?: string
  updated_at?: string
}

export type IdentityVerificationPayload = {
  status: IdentityVerificationStatus
  verification: VerificationRecord | null
  documents: IdentityDocumentRecord[]
}

export type SupportReviewDocument = {
  id: number
  document_type: string
  file_url?: string | null
  file_name?: string | null
  file_mime?: string | null
  file_size?: number | null
  version?: number
  status?: string
  created_at?: string
  sealed?: boolean
}

export type SupportReviewPhoto = {
  id: number
  url: string
  name?: string | null
}

export type SupportVerificationRecord = VerificationRecord & {
  documents_count?: number
  documents?: SupportReviewDocument[]
  user_role?: 'owner' | 'tenant' | 'support' | 'admin' | string
}

export type PropertyVerificationRecord = {
  id: number
  property_fk: number
  responsible_user_fk: string
  relation_to_property: 'owner' | 'current_tenant' | 'third_party'
  status: ListingManagerStatus
  property_title?: string | null
  documents_count?: number
  documents?: SupportReviewDocument[]
  photos?: SupportReviewPhoto[]
  approved_by_user_fk?: string | null
  reviewed_by_user_fk?: string | null
  rejection_reason?: string | null
  internal_notes?: string | null
  visible_message?: string | null
  reviewed_at?: string | null
  approved_at?: string | null
  rejected_at?: string | null
  correction_requested_at?: string | null
  created_at?: string
  updated_at?: string
}

type ApiResponse<T> = {
  success: boolean
  message?: string
  data?: T
}

function unwrap<T>(response: ApiResponse<T>): T {
  if (!response.success || response.data === undefined) {
    throw new Error(response.message || 'Request failed')
  }
  return response.data
}

export async function startIdentityVerification(): Promise<IdentityVerificationPayload> {
  const res = await axiosInstance.post<ApiResponse<IdentityVerificationPayload>>(
    ENDPOINTS.VERIFICATION.START_IDENTITY
  )
  return unwrap(res.data)
}

export async function uploadIdentityDocument(params: {
  documentType: string
  file: Record<string, unknown>
}): Promise<IdentityVerificationPayload> {
  const res = await axiosInstance.post<ApiResponse<IdentityVerificationPayload>>(
    ENDPOINTS.VERIFICATION.IDENTITY_DOCUMENTS,
    params
  )
  return unwrap(res.data)
}

export async function loadMyVerification(): Promise<IdentityVerificationPayload> {
  const res = await axiosInstance.get<ApiResponse<IdentityVerificationPayload>>(
    ENDPOINTS.VERIFICATION.ME_VERIFICATION
  )
  return unwrap(res.data)
}

export async function loadSupportIdentityVerifications(
  status?: IdentityVerificationStatus
): Promise<SupportVerificationRecord[]> {
  const res = await axiosInstance.get<ApiResponse<SupportVerificationRecord[]>>(
    ENDPOINTS.VERIFICATION.SUPPORT_IDENTITY_VERIFICATIONS,
    { params: status ? { status } : undefined }
  )
  return unwrap(res.data)
}

export async function reviewIdentityVerification(params: {
  id: number
  action?: 'approve' | 'reject' | 'request_correction' | 'start_review'
  status?: IdentityVerificationStatus
  rejectionReason?: string
  internalNotes?: string
  visibleMessage?: string
}): Promise<VerificationRecord> {
  const { id, ...payload } = params
  const res = await axiosInstance.patch<ApiResponse<VerificationRecord>>(
    `${ENDPOINTS.VERIFICATION.SUPPORT_IDENTITY_VERIFICATIONS}/${id}`,
    payload
  )
  return unwrap(res.data)
}

export async function loadSupportPropertyVerifications(
  status?: ListingManagerStatus
): Promise<PropertyVerificationRecord[]> {
  const res = await axiosInstance.get<ApiResponse<PropertyVerificationRecord[]>>(
    ENDPOINTS.VERIFICATION.SUPPORT_PROPERTY_VERIFICATIONS,
    { params: status ? { status } : undefined }
  )
  return unwrap(res.data)
}

export async function reviewPropertyVerification(params: {
  id: number
  action?: 'approve' | 'reject' | 'request_correction' | 'start_review'
  status?: ListingManagerStatus
  rejectionReason?: string
  internalNotes?: string
  visibleMessage?: string
}): Promise<PropertyVerificationRecord> {
  const { id, ...payload } = params
  const res = await axiosInstance.patch<ApiResponse<PropertyVerificationRecord>>(
    `${ENDPOINTS.VERIFICATION.SUPPORT_PROPERTY_VERIFICATIONS}/${id}`,
    payload
  )
  return unwrap(res.data)
}
